<template>
  <div class="relative">
    <Table
      :items="dataFetcher.data"
      :columns="fieldsFulfillmentNonKomship"
      :loading="dataFetcher.isLoading"
      empty-text="Tidak ada data yang ditampilkan."
      max-height="50vh"
      sticky-header
    >
      <template #cell(outbound_id)="data">
        <p class="mb-0">
          {{ data.item.outbound_no || data.value }}
        </p>
        <p
          v-if="data.item.is_printed"
          class="text-[#4285F4] text-[12px] mb-0"
        >
          Label telah diunduh
        </p>
        <p
          v-else
          class="text-[#F95031] text-[12px] mb-0"
        >
          Label belum diunduh
        </p>
      </template>
      <template #cell(created_at)="data">
        <p class="mb-0">
          {{ DAY_MONTH_YEAR(data.value) }}
        </p>
        <p class="text-[#828282] mb-0">
          {{ TIME(data.value) }}
        </p>
      </template>
      <template #cell(partner_name)="data">
        <p class="mb-0">
          {{ data.value || '-' }}
        </p>
        <p class="text-[#828282] mb-0">
          {{ data.item.brand_name || '-' }}
        </p>
      </template>
      <template #cell(status)="data">
        <StatusOrderPill
          :status="data.value"
          :mapping="statusMapping"
        />
      </template>
      <template #cell(action)="data">
        <router-link
          :to="{ name: 'kompack-total-nilai-transaksi-fulfillment-nonkomship-detail', params: { id: data.item.outbound_id } }"
          class="text-[#F95031] hover:underline"
        >
          Lihat Detail
        </router-link>
      </template>
      <template #footer>
        <div
          v-if="dataFetcher.isLoading && dataFetcher.data.length > 0"
          class="flex justify-center items-center p-[20px] bg-white bg-opacity-90"
        >
          <div class="flex items-center gap-2">
            <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600" />
            <span class="text-sm text-gray-600">Memuat data...</span>
          </div>
        </div>
        <div
          ref="loadMoreSentinel"
          class="h-[1px]"
        />
      </template>
    </Table>
  </div>
</template>

<script>
import _ from 'lodash'
import { Table } from '@core/components/ui'
import { createPaginatedFetcher } from '@/@core/libs/fetch/usePaginatedFetcher'
import { DAY_MONTH_YEAR, YEAR_MONTH_DAY, TIME } from '@/libs/filterDate'
import { fieldsFulfillmentNonKomship } from '../config'
import { handleStatusClasses, statusMapping } from '../fulfillment/nonkomship/config'
import StatusOrderPill from './StatusOrderPill.vue'

/**
 * Component untuk menampilkan data fulfillment non-komship
 * @component FulfillmentNonKomship
 */
export default {
  components: {
    Table,
    StatusOrderPill,
  },
  props: {
    /** @type {Object} Periode tanggal untuk filter data */
    period: {
      type: Object,
      required: true,
    },
    status: {
      type: String,
      default: null,
    },
    search: {
      type: String,
      default: '',
    },
    warehouseId: {
      type: [Number, String],
      default: null,
    },
    partnerId: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      dataFetcher: null,
      statusMapping,
      observer: null,
    }
  },
  watch: {
    status() {
      this.handleRefreshData()
    },
    search() {
      this.debouncedRefresh()
    },
    warehouseId() {
      this.handleRefreshData()
    },
    partnerId() {
      this.handleRefreshData()
    },
    'dataFetcher.data': {
      handler() {
        this.$nextTick(() => {
          if (this.observer && this.$refs.loadMoreSentinel) {
            this.observer.unobserve(this.$refs.loadMoreSentinel)
            this.observer.observe(this.$refs.loadMoreSentinel)
          }
        })
      },
    },
  },
  created() {
    this.initializeFetcher()
    this.fieldsFulfillmentNonKomship = fieldsFulfillmentNonKomship
    this.debouncedRefresh = _.debounce(this.handleRefreshData, 500)
  },
  mounted() {
    this.handleRefreshData()
    this.initObserver()
  },
  beforeDestroy() {
    if (this.debouncedRefresh && this.debouncedRefresh.cancel) {
      this.debouncedRefresh.cancel()
    }
    if (this.observer) {
      this.observer.disconnect()
    }
  },
  methods: {
    DAY_MONTH_YEAR,
    YEAR_MONTH_DAY,
    TIME,
    handleStatusClasses,
    /**
     * Inisialisasi paginated fetcher untuk data fulfillment
     */
    initializeFetcher() {
      const fnFetch = ({
        offset, limit, start_date, end_date, status, search, warehouse_id: warehouseId, partner_id: partnerId,
      }) => this.$http_new.get('/kompack/api/v1/dashboard/admin/outbounds', {
        params: {
          start_date,
          end_date,
          offset,
          limit,
          status: status || undefined,
          outbound_id: search || undefined,
          warehouse_id: warehouseId || undefined,
          partner_id: partnerId || undefined,
        },
      }).then(res => {
        const { data } = res.data
        return {
          data,
        }
      }).catch(() => ({
        data: [],
      }))

      this.dataFetcher = createPaginatedFetcher(fnFetch, 0, 50)
    },
    async handleRefreshData() {
      if (this.debouncedRefresh && this.debouncedRefresh.cancel) {
        this.debouncedRefresh.cancel()
      }
      await this.dataFetcher.fetchData({
        reset: true,
        options: {
          start_date: YEAR_MONTH_DAY(this.period.startDate),
          end_date: YEAR_MONTH_DAY(this.period.endDate),
          status: this.status || undefined,
          search: this.search || undefined,
          warehouse_id: this.warehouseId || undefined,
          partner_id: this.partnerId || undefined,
        },
      })
    },
    /**
     * Inisialisasi IntersectionObserver untuk infinite loading via sentinel di #footer
     */
    initObserver() {
      if (!this.$refs.loadMoreSentinel) return
      this.observer = new IntersectionObserver(
        entries => {
          const entry = entries[0]
          if (entry && entry.isIntersecting && !this.dataFetcher.isLoading) {
            if (this.dataFetcher.data.length > 0 && this.dataFetcher.hasMore) {
              this.dataFetcher.fetchData()
            }
          }
        },
        {
          rootMargin: '100px',
          threshold: 0,
        },
      )
      this.observer.observe(this.$refs.loadMoreSentinel)
    },
  },
}
</script>
